import React, { useEffect } from 'react';
import { FiZap, FiAlertTriangle, FiArrowUpCircle, FiRefreshCw } from 'react-icons/fi';

export default function BandwidthRestrictedModal({ bandwidthInfo, onRecheck, checking }) {
    const usedGB   = bandwidthInfo?.monthlyUsedGB  ?? 0;
    const limitGB  = bandwidthInfo?.monthlyLimitGB ?? 0;
    const usagePct = bandwidthInfo?.usagePercent   ?? 100;
    const barWidth = Math.min(100, usagePct);

    // Lock body scroll while modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.65)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
            }}
        >
            {/* ── Card ── */}
            <div
                style={{
                    background: '#ffffff',
                    borderRadius: '20px',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
                    maxWidth: '520px',
                    width: '100%',
                    overflow: 'hidden',
                    animation: 'bwModalIn 0.25s ease-out',
                }}
            >
                {/* Top accent bar */}
                <div style={{ height: '5px', background: 'linear-gradient(90deg, #f59e0b, #ef4444)' }} />

                <div style={{ padding: '2rem 2.25rem 1.75rem' }}>

                    {/* Icon + badge + heading */}
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                        <div style={{
                            width: '64px', height: '64px',
                            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 1rem',
                            boxShadow: '0 4px 16px rgba(245,158,11,0.25)',
                        }}>
                            <FiZap size={28} color="#f59e0b" />
                        </div>

                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            background: '#fef3c7', color: '#b45309',
                            fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
                            textTransform: 'uppercase', padding: '4px 12px',
                            borderRadius: '999px', marginBottom: '0.75rem',
                        }}>
                            <FiAlertTriangle size={11} />
                            Bandwidth Limit Reached
                        </div>

                        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.4rem' }}>
                            Access Temporarily Restricted
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
                            Your platform has consumed <strong>{usagePct}%</strong> of its monthly
                            bandwidth allowance. Admin features are paused until your plan is upgraded.
                        </p>
                    </div>

                    {/* ── Usage meter ── */}
                    <div style={{
                        background: '#f9fafb', border: '1px solid #f3f4f6',
                        borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem',
                    }}>
                        {/* Header row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                Monthly Bandwidth
                            </span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#ef4444' }}>
                                {usagePct}%
                            </span>
                        </div>

                        {/* Progress bar */}
                        <div style={{ height: '10px', borderRadius: '999px', background: '#e5e7eb', overflow: 'hidden', marginBottom: '12px' }}>
                            <div style={{
                                height: '100%',
                                width: `${barWidth}%`,
                                borderRadius: '999px',
                                background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
                                transition: 'width 0.8s ease-in-out',
                            }} />
                        </div>

                        {/* Storage used & consumed stat cards */}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {/* Bandwidth Used */}
                            <div style={{
                                flex: 1,
                                background: '#fff7ed',
                                border: '1px solid #fed7aa',
                                borderRadius: '8px',
                                padding: '8px 12px',
                            }}>
                                <p style={{ fontSize: '10px', fontWeight: 600, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '0 0 2px' }}>
                                    Bandwidth Used
                                </p>
                                <p style={{ fontSize: '16px', fontWeight: 700, color: '#ea580c', margin: 0 }}>
                                    {usedGB.toFixed(2)} <span style={{ fontSize: '11px', fontWeight: 500, color: '#c2410c' }}>GB</span>
                                </p>
                            </div>

                            {/* Monthly Limit */}
                            <div style={{
                                flex: 1,
                                background: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '8px',
                                padding: '8px 12px',
                            }}>
                                <p style={{ fontSize: '10px', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '0 0 2px' }}>
                                    Monthly Limit
                                </p>
                                <p style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a', margin: 0 }}>
                                    {limitGB.toFixed(2)} <span style={{ fontSize: '11px', fontWeight: 500, color: '#15803d' }}>GB / mo</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <a
                            href="/"
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                color: '#ffffff', fontWeight: 700, fontSize: '14px',
                                padding: '13px 24px', borderRadius: '12px',
                                textDecoration: 'none',
                                boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
                                transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                        >
                            <FiArrowUpCircle size={15} />
                            Upgrade Plan
                        </a>

                        <button
                            onClick={onRecheck}
                            disabled={checking}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                background: 'transparent',
                                border: '1.5px solid #e5e7eb',
                                color: '#6b7280', fontWeight: 600, fontSize: '13px',
                                padding: '11px 24px', borderRadius: '12px',
                                cursor: checking ? 'not-allowed' : 'pointer',
                                opacity: checking ? 0.6 : 1,
                                transition: 'all 0.2s',
                            }}
                        >
                            <FiRefreshCw size={13} style={{ animation: checking ? 'bwSpin 1s linear infinite' : 'none' }} />
                            {checking ? 'Checking…' : 'Re-check Status'}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '0.75rem 2.25rem 1.25rem',
                    borderTop: '1px solid #f3f4f6',
                    textAlign: 'center', color: '#9ca3af', fontSize: '11px',
                }}>
                    Need help? Contact support to upgrade or request an allowance increase.
                </div>
            </div>

            <style>{`
                @keyframes bwModalIn {
                    from { opacity: 0; transform: scale(0.94) translateY(12px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);    }
                }
                @keyframes bwSpin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
